import browser from "web/script/utils/browser";
import criteo from "web/script/embedded/criteo";
import environment from "web/script/modules/environment";


const COUNTRY_ACCOUNTS_MAP = {
    US: "US",
    GB: "GB",
    AL: "EU",
    AD: "EU",
    AM: "EU",
    AZ: "EU",
    BY: "EU",
    BE: "EU",
    BA: "EU",
    BG: "EU",
    HR: "EU",
    CY: "EU",
    CZ: "EU",
    DK: "EU",
    EE: "EU",
    FI: "EU",
    FR: "EU",
    FX: "EU",
    GE: "EU",
    DE: "EU",
    GR: "EU",
    HU: "EU",
    IS: "EU",
    IE: "EU",
    IT: "EU",
    LV: "EU",
    LI: "EU",
    LT: "EU",
    LU: "EU",
    MK: "EU",
    MT: "EU",
    MD: "EU",
    MC: "EU",
    ME: "EU",
    NL: "EU",
    AN: "EU",
    NO: "EU",
    PL: "EU",
    PT: "EU",
    RO: "EU",
    RU: "EU",
    SM: "EU",
    RS: "EU",
    SK: "EU",
    SI: "EU",
    ES: "EU",
    SE: "EU",
    CH: "EU",
    TR: "EU",
    UA: "EU",
    VA: "EU",
    AT: "EU",
};

const ACCOUNTS = {
    US: 9843,
    GB: 30089,
    EU: 36542,
    ROW: 36541,
};
const NEW_ACCOUNTS = {
    US: 55622,
    GB: 58186,
};

const LEAD_EVENT_ACCOUNTS = { ...NEW_ACCOUNTS, EU: 36542 };

const USER_SEGMENTS = {
    GB: {
        "Google-PLA-CSS": "1",
        "Bing-PLA": "2",
    },
    US: {
        "Bing-PLA": "1",
    },
};

/**
 * Get the custom Criteo viewport types.
 */
function getCriteoViewport() {
    switch (browser.getViewport()) {
        case browser.VIEWPORTS.MOBILE:
            return "m";
        case browser.VIEWPORTS.TABLET:
            return "t";
        case browser.VIEWPORTS.DESKTOP:
            return "d";
        default:
            return "";
    }
}

function onLead(account, region, event) {
    const transactionID = event.data.session_id + (region === "GB" ? "-aff" : "");
    // https://jira.lystit.com/browse/AT-997 - more info on what to send to criteo
    const item = {
        id: event.data.label,
        price: event.data.customData.sale_price,
        quantity: "1",
    };
    const flush = { event: "flushEvents" };
    const setSiteEvent = { event: "setSiteType", type: getCriteoViewport() };
    const setEmailEvent = { event: "setEmail", email: "" };

    criteo({ event: "manualFlush" });

    // Fire track transaction pixel for existing accounts.
    // Do not do this for EU since we will be doing this again when sending the 'Sale' event.
    if (region != "EU") {
        criteo(
            { event: "setAccount", account },
            setSiteEvent,
            setEmailEvent,
            {
                event: "trackTransaction",
                id: transactionID,
                item: [item],
            },
            flush
        );
    }

    // Send 'Sale' event to lead event account if traffic source is Criteo otherwise 'Add to Cart'
    if (!(region in LEAD_EVENT_ACCOUNTS)) {
        return;
    }

    let leadEvent;
    if (environment.get("trafficSource") != "criteo") {
        leadEvent = { event: "viewBasket", item: [item] };
    } else {
        leadEvent = { event: "trackTransaction", id: transactionID, item: [item] };
    }

    criteo(
        { event: "setAccount", account: LEAD_EVENT_ACCOUNTS[region] },
        setSiteEvent,
        setEmailEvent,
        leadEvent,
        flush
    );
}

export default {
    send(data) {
        // filter out only the events for page views in the US or lead events
        const events = data.reduce((prev, curr) => {
            if (
                curr.type === "page_view" ||
                curr.data.action === "lead" ||
                curr.data.action === "lead_sold_out" ||
                curr.type === "criteo_event"
            ) {
                prev.push(curr);
            }
            return prev;
        }, []);

        const userId = environment.get("userId");
        const country = environment.get("country");

        const region = COUNTRY_ACCOUNTS_MAP[country] || "ROW";
        let accounts = [ACCOUNTS[region]];

        if (country in NEW_ACCOUNTS) {
            accounts.push(NEW_ACCOUNTS[country]);
        }

        // iterate over the events and send them to Criteo
        events.forEach((event) => {
            let criteoEvent;

            if (event.data.customData && event.data.customData.isCssLead) {
                const item = {
                    id: event.data.label,
                    price: event.data.customData.sale_price,
                    quantity: "1",
                };
                criteoEvent = { event: "viewBasket", item: [item] };
                const userSegments = USER_SEGMENTS[country] || {};
                const atcGrouping = event.data.customData.atc_grouping;
                if (atcGrouping in userSegments) {
                    criteoEvent["user_segment"] = userSegments[atcGrouping];
                }
            } else if (event.data.action === "lead" || event.data.action === "lead_sold_out") {
                onLead(accounts[0], region, event);
            } else if (
                event.data.page_type === "product" ||
                event.data.page_type === "product_overlay"
            ) {
                if (userId && event.data.product_is_icon) {
                    return Promise.resolve();
                }
                criteoEvent = { event: "viewItem", item: event.data.product_id };
            } else if (event.data.eventName === "criteo_product_ids") {
                criteoEvent = { event: "viewList", item: event.data.customData.ids };
            } else if (event.data.eventName === "criteo_cart_items") {
                criteoEvent = { event: "viewBasket", item: event.data.customData.cartItems };
            } else if (event.data.page_type === "homepage") {
                criteoEvent = { event: "viewHome" };
            }
            if (criteoEvent) {
                criteo({ event: "manualFlush" });
                accounts.forEach((account) =>
                    criteo(
                        { event: "setAccount", account },
                        { event: "setSiteType", type: getCriteoViewport() },
                        { event: "setEmail", email: "" },
                        criteoEvent,
                        { event: "flushEvents" }
                    )
                );
            }
        });
        // no way of waiting for this to be done
        return Promise.resolve();
    },
};
